import type {AccountWithCollectionAccess} from '@src/types/accessControl.ts';
import type {ResponseError} from '@src/types/errors.ts';
import type {UseMutationOptions} from '@tanstack/react-query';
import type {AxiosResponse} from 'axios';
import {request} from '@src/api/index';
import API_URLS from '@src/api/urls.ts';
import {DURATION_MS} from '@src/api/util.js';
import {notifyError, notifySuccess} from '@src/components/notifier/notify.tsx';
import {extractShErrorMessage} from '@src/utils/shErrors';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

type RemoveDataCollectionSharedAccountParams = {
  collectionId: string;
  accountId: string;
};

type AddDataCollectionSharedAccountParams = {
  collectionId: string;
  accountId: string;
  notes: string;
};

type UpdateDataCollectionAclParams = {
  collectionId: string;
  enabled: boolean;
};

const DATA_COLLECTION_SHARED_ACCOUNTS_PAGE_SIZE = 10;

// -------------------------
// Fetch functions

const getDataCollectionSharedAccounts = (collectionId: string) =>
  request
    .get(`${API_URLS.accessControl}/collection/${collectionId}/da`, {
      params: {
        count: DATA_COLLECTION_SHARED_ACCOUNTS_PAGE_SIZE,
      },
    })
    .then((res: AxiosResponse) => res.data);

const addDataCollectionSharedAccount = ({
  collectionId,
  accountId,
  notes,
}: AddDataCollectionSharedAccountParams) =>
  request
    .post(
      `${API_URLS.accessControl}/collection/${collectionId}/da/${accountId}/USE`,
      {params: {notes}},
    )
    .then((res: AxiosResponse) => res.data);

const removeDataCollectionSharedAccount = ({
  collectionId,
  accountId,
}: RemoveDataCollectionSharedAccountParams) =>
  request
    .delete(
      `${API_URLS.accessControl}/collection/${collectionId}/da/${accountId}/USE`,
    )
    .then((res: AxiosResponse) => res.data);

const updateDataCollectionAclStatus = ({
  collectionId,
  enabled,
}: UpdateDataCollectionAclParams) =>
  request.post(
    `${API_URLS.accessControl}/collection/${collectionId}/acl-enabled/${enabled}`,
  );

const getIsPrivateDataCollection = (collectionId: string) =>
  request
    .get(`${API_URLS.accessControl}/collection/${collectionId}/acl-enabled`)
    .then((res: AxiosResponse) => res.data);

const getRestrictions = (accountId: string) =>
  request
    .get(`${API_URLS.accessControl}/account/${accountId}`)
    .then((res: AxiosResponse) => res.data)
    .catch((error: ResponseError) => {
      // The API returns 404 when there are no restrictions (:facepalm:),
      // so we want to swallow that error but let all others through
      if (error?.response?.status === 404) {
        return {restrictions: [] as string[]};
      } else {
        throw error;
      }
    });

// -------------------------
// Hooks

export const useGetDataCollectionSharedAccounts = (collectionId: string) => {
  return useInfiniteQuery({
    queryKey: ['dataCollectionSharedAccounts', collectionId],
    queryFn: ({pageParam: nextUrl}: {pageParam?: string}) =>
      nextUrl
        ? request.get(nextUrl).then((res: AxiosResponse) => res.data)
        : getDataCollectionSharedAccounts(collectionId),
    getNextPageParam: lastPage => lastPage.links?.next,
    select: data =>
      data.pages.flatMap((page: {data: AccountWithCollectionAccess[]}) =>
        page.data.map(account => ({...account, id: account.domainAccountId})),
      ),
    initialPageParam: '',
    staleTime: DURATION_MS.HOUR,
  });
};

export const useAddDataCollectionSharedAccount = (queryOptions = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: addDataCollectionSharedAccount,
    ...queryOptions,
    onSuccess: (_, {collectionId, accountId}) => {
      notifySuccess({
        data: {message: `Access granted to account ${accountId}.`},
      });
      queryClient.invalidateQueries({
        queryKey: ['dataCollectionSharedAccounts', collectionId],
      });
    },
    onError: (error, {accountId}) => {
      notifyError({
        data: {
          title: `Failed to add access for account ${accountId}`,
          message: extractShErrorMessage(error),
        },
      });
    },
  });
};

export const useRemoveDataCollectionSharedAccount = (
  queryOptions: UseMutationOptions<
    unknown,
    void,
    RemoveDataCollectionSharedAccountParams,
    unknown
  > = {},
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: removeDataCollectionSharedAccount,
    ...queryOptions,
    onSuccess: (data, variables, onMutateResult, context) => {
      if (queryOptions.onSuccess) {
        queryOptions.onSuccess(data, variables, onMutateResult, context);
      }

      return queryClient.invalidateQueries({
        queryKey: ['dataCollectionSharedAccounts', variables.collectionId],
      });
    },
    onError: (error, {accountId}) => {
      notifyError({
        data: {
          title: `Failed to remove access for account ${accountId}`,
          message: extractShErrorMessage(error),
        },
      });
    },
  });
};

export const useIsPrivateDataCollection = (
  collectionId: string,
  queryOptions = {},
) => {
  return useQuery({
    queryKey: ['isPrivateDataCollection', collectionId],
    queryFn: () => getIsPrivateDataCollection(collectionId),
    staleTime: DURATION_MS.HOUR,
    ...queryOptions,
  });
};

export const useUpdateDataCollectionAclStatus = (
  queryOptions: UseMutationOptions<
    unknown,
    void,
    UpdateDataCollectionAclParams,
    unknown
  > = {},
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateDataCollectionAclStatus,
    ...queryOptions,
    onSuccess: (_data, params, onMutate, context) => {
      notifySuccess({
        data: {message: 'Collection status changed.'},
      });
      if (queryOptions.onSuccess) {
        queryOptions.onSuccess(_data, params, onMutate, context);
      }

      queryClient.invalidateQueries({
        queryKey: ['isPrivateDataCollection', params.collectionId],
      });
    },
    onError: (error, params, onMutateResult, context) => {
      notifyError({
        data: {
          title: 'Changing collection status failed',
          message: extractShErrorMessage(error),
        },
      });

      if (queryOptions.onError) {
        queryOptions.onError(error, params, onMutateResult, context);
      }
    },
  });
};

export const useGetRestrictions = (accountId: string, queryOptions = {}) => {
  return useQuery({
    queryKey: ['restrictions', accountId],
    queryFn: () => getRestrictions(accountId),
    staleTime: DURATION_MS.HOUR,
    ...queryOptions,
  });
};
